import { figmaColorScales, figmaMainColors, figmaSemanticColors } from '../../../core/tokens/colors';
import { styleguideColorsExtended as palette, styleguideRadius, styleguideSpacing } from '../../../core/tokens/styleguide';

/**
 * KAIDEV-3592: ReviewFlow tokens now derive from the 2026 Kai Styleguide palette
 * (styleguideColors) rather than the figmaColorScales. The 7-color styleguide
 * palette replaces the broader figma scale for ReviewFlow surfaces; intent
 * colors (success/info/warning/error) still draw from figmaSemanticColors until
 * the styleguide defines equivalents. Radii collapse to sharp (0) + circle (50%)
 * — no intermediate radii. Spacing maps onto the 4px-base styleguide scale.
 */
export const REVIEW_COLORS = {
  // Core surfaces + text from the styleguide palette.
  canvas: palette.paper,
  surface: palette.white,
  surfaceMuted: palette.surfaceMuted,
  surfaceEmphasis: palette.stroke,
  toolbar: palette.ink,
  toolbarText: palette.paper,
  toolbarTextMuted: 'rgba(255, 253, 248, 0.72)',
  toolbarAccent: palette.accent,
  text: palette.ink,
  textMuted: palette.midtone,
  textSoft: palette.midtone,
  textInverse: palette.paper,
  border: palette.stroke,
  borderStrong: palette.midtone,
  borderHeavy: palette.ink,
  // Intent colors stay on the figma semantic scale until the styleguide
  // defines success/info/warning/scope equivalents.
  success: figmaSemanticColors.intent.success.default,
  successSubtle: figmaSemanticColors.intent.success.subtle,
  info: figmaSemanticColors.intent.info.default,
  infoSubtle: figmaSemanticColors.intent.info.subtle,
  scope: figmaSemanticColors.intent.scope.default,
  scopeSubtle: figmaSemanticColors.intent.scope.subtle,
  warning: figmaSemanticColors.intent.warning.default,
  warningSubtle: figmaSemanticColors.intent.warning.subtle,
  error: palette.error,
  errorSubtle: figmaSemanticColors.intent.error.subtle,
} as const;

/** Radii: the styleguide is sharp-cornered. Only `circle` (50%) survives for
 *  status dots / avatars; everything else is 0. The named steps (small/medium/
 *  large/xLarge/pill) are kept for API compatibility but now resolve to 0 so
 *  existing component code updates without a sweep. */
export const REVIEW_RADII = {
  small: styleguideRadius.sharp,
  medium: styleguideRadius.sharp,
  large: styleguideRadius.sharp,
  xLarge: styleguideRadius.sharp,
  pill: styleguideRadius.sharp,
  circle: styleguideRadius.circle,
} as const;

/** Spacing: maps onto the styleguide 4px-base scale. */
export const REVIEW_SPACING = {
  xSmall: styleguideSpacing.s1,
  small: styleguideSpacing.s2,
  medium: styleguideSpacing.s3,
  large: styleguideSpacing.s4,
  xLarge: 20,
  xxLarge: styleguideSpacing.s5,
} as const;

export const REVIEW_ELEVATION = {
  card: '0 2px 8px rgba(0, 0, 0, 0.05)',
  overlay: '0 20px 60px rgba(0, 0, 0, 0.45)',
} as const;

export const REVIEW_CONTROL_SIZES = {
  iconButton: 32,
  compactIconButton: 26,
  minInteractiveHeight: 28,
  toolbarHeight: 52,
} as const;
